/**
 * This module contains common select auto complete reusable filters
 */

import { useMemo } from "react";
import { toMonthShortName, Nil, toNumber, toInt } from "cy-util";

/**
 * A basic string filter
 * @param query
 * @param name
 */
export function selectStringFilter(query: string, name: string) {
  return name
    .toLowerCase()
    .replace(/\s+/g, "")
    .includes(query.toLowerCase().replace(/\s+/g, ""));
}

/**
 * A basic number filter that can handle leading zeroes
 * @param allowedValues
 * @param query
 */
export function useNumberFilter(
  allowedValues: readonly number[],
  query: string | Nil
) {
  return useMemo(
    () =>
      allowedValues.filter((x) =>
        x.toString().includes((toInt(query) || "").toString())
      ),
    [query, allowedValues]
  );
}

/**
 * A month filter which can handle months entered as strings or numbers
 * eg Dec or 12
 * @param allowedMonthNumbers
 * @param query
 */
export function useMonthFilter(
  allowedMonthNumbers: readonly number[],
  query: string | Nil
) {
  const queryString = query == null ? "" : query;

  return useMemo(
    () =>
      allowedMonthNumbers.filter((month) => {
        const monthNumberQuery = toNumber(query);
        if (/[a-z]/i.test(queryString)) {
          return toMonthShortName(month)
            .toLowerCase()
            .includes(queryString.toLowerCase());
        }
        return month.toString().includes((monthNumberQuery || "").toString()); // remove leading zeros
      }),
    [queryString, allowedMonthNumbers]
  );
}
